import { useToast } from "@/components/ui/use-toast.ts";
import { fetchApi } from "@/lib/api/index.ts";
import { User } from "@/lib/api/user.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { z } from "zod";

export type MentorshipDetails = {
  id: string;
  created_at: string;
  sdk_url: string;
  last_communicated: Date | null;
  locked: boolean;
  mentor: User;
  mentee: User;
  regular_call_offset: number | null;
  regular_call_day: string | null;
  regular_call_cadence: string | null;
  message_cadence: string | null;
  mentee_not_available: string | null;
  deactivated: boolean;
  metadata?: {
    override?: {
      isOverridden: boolean;
      adminId: string | null;
      adminName: string | null;
      startedAt: string | null;
    };
    ai_traits?: string;
    customName?: string;
  };
};

export const useGetMentorship = (mentorshipId: string) => {
  return useQuery({
    queryKey: ["mentorship", mentorshipId],
    queryFn: async () => {
      const mentorship = await fetchApi<{ mentorship: MentorshipDetails }>({
        url: `/v1/mentorships/${mentorshipId}`,
        method: "GET",
      });

      if (!mentorship.data?.mentorship) throw new Error("No mentorship found");

      return mentorship.data.mentorship;
    },
    enabled: !!mentorshipId,
  });
};

export const createMentorshipSchema = z.object({
  mentorId: z.string(),
  menteeId: z.string(),
});

export type CreateMentorshipInput = z.infer<typeof createMentorshipSchema>;

export const useCreateMentorship = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<void, Error, CreateMentorshipInput>({
    mutationKey: ["create-mentorship"],
    mutationFn: async (input) => {
      await fetchApi({
        url: "/v1/mentorships",
        method: "POST",
        data: input,
      });
    },
    onSuccess: () => {
      toast({
        title: "Mentorship created",
        description: "The mentorship has been created successfully",
      });
    },
    onError: (error) => {
      let description: undefined | string = undefined;

      if (error instanceof AxiosError) {
        description = error.response?.data?.message;
      }

      toast({
        title: "Something went wrong",
        description,
        variant: "destructive",
      });
    },
    onSettled: async (_, __, input) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["user-mentorships", input.mentorId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["user-mentorships", input.menteeId],
        }),
      ]);
    },
  });
};

export const useUpdateMentorship = (userId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<
    void,
    Error,
    { mentorshipId: string; deactivated: boolean }
  >({
    mutationKey: ["update-mentorship"],
    mutationFn: async ({ mentorshipId, deactivated }) => {
      await fetchApi({
        url: `/v1/mentorships/${mentorshipId}`,
        method: "PATCH",
        data: { deactivated },
      });
    },
    onSuccess: () => {
      toast({
        title: "Mentorship updated",
        description: "The mentorship has been updated successfully",
      });
    },
    onError: (error) => {
      let description: undefined | string = undefined;

      if (error instanceof AxiosError) {
        description = error.response?.data?.message;
      }

      toast({
        title: "Something went wrong",
        description,
        variant: "destructive",
      });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["user-mentorships", userId],
      });
    },
  });
};

interface MagicMomentDismissFields {
  day1_banner_dismissed?: boolean;
  day2_modal_dismissed?: boolean;
  day2_banner_dismissed?: boolean;
}

export const useUpdateMagicMomentState = () => {
  // Fire-and-forget: we intentionally do NOT invalidate the mentorship query.
  // Dismissal persists to BE so it takes effect on next refresh/navigation,
  // but the current UI stays visible so the mentor can finish browsing.
  return useMutation<
    void,
    Error,
    { mentorshipId: string; fields: MagicMomentDismissFields }
  >({
    mutationKey: ["update-magic-moment-state"],
    mutationFn: async ({ mentorshipId, fields }) => {
      await fetchApi({
        url: `/v2/mentorships/${mentorshipId}/magic-moment`,
        method: "PATCH",
        data: fields,
      });
    },
  });
};

export const useUpdateMentorshipCustomName = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<void, Error, { mentorshipId: string; customName: string }>(
    {
      mutationKey: ["update-mentorship-custom-name"],
      mutationFn: async ({ mentorshipId, customName }) => {
        await fetchApi({
          url: `/v2/mentorships/${mentorshipId}/custom-name`,
          method: "PUT",
          data: { customName },
        });
      },
      onSuccess: () => {
        toast({
          title: "Hub name updated",
          description: "The hub name has been updated successfully",
        });
      },
      onError: (error) => {
        let description: undefined | string = undefined;

        if (error instanceof AxiosError) {
          description = error.response?.data?.message;
        }

        toast({
          title: "Something went wrong",
          description,
          variant: "destructive",
        });
      },
      onSettled: async () => {
        await queryClient.invalidateQueries({
          queryKey: ["mentorship"],
        });
      },
    },
  );
};

export interface Mentorship {
  mentorship_id: string;
  mentor_id: string;
  mentee_id: string;
  mentor_name: string;
  mentee_name: string;
  mentor_profile_picture_url: string;
  mentee_profile_picture_url: string;
  mentor_deactivated: boolean;
  mentee_deactivated: boolean;
  deactivated: boolean;
  start_date: string;
  days_in_mentorship: string;
}

export const useGetUserMentorships = (userId: string, enabled = true) => {
  return useQuery({
    queryKey: ["user-mentorships", userId],
    queryFn: async () => {
      const mentorships = await fetchApi<{ mentorships: Mentorship[] }>({
        url: `/v2/users/${userId}/mentorships`,
        method: "GET",
      });

      if (!mentorships.data?.mentorships)
        throw new Error("No mentorships found");

      return mentorships.data.mentorships;
    },
    enabled: !!userId && enabled,
  });
};

interface TeenWellBeing {
  id?: string;
  created_at?: string;
  gad2Response?: number;
  phq2Response?: number;
  gad8Response?: number;
  phq8Response?: number;
  riskResponse?: "Y" | "N";
  riskResponseNew?: "Y" | "N";
}

interface Rating {
  id?: string;
  rating?: number;
  type?: string;
  created_at?: string;
  responses?: {
    question?: string;
    response?: string;
  }[];
}

export interface MentorshipFeedback {
  match: {
    response: string;
    createdAt: string;
  };
  continue: {
    response: string;
    createdAt: string;
  };
  averageRating: number;
  ratingList: Rating[];
  teenWellBeing: {
    current: TeenWellBeing;
    history: TeenWellBeing[];
  };
}

export const useGetMentorshipFeedback = (mentorshipId?: string) => {
  return useQuery<MentorshipFeedback>({
    queryKey: ["surveys", mentorshipId],
    queryFn: async () => {
      const feedback = await fetchApi<MentorshipFeedback>({
        url: `/v2/admin/mentorships/${mentorshipId}/surveys`,
        method: "GET",
      });

      if (!feedback.data) throw new Error("No feedback found");

      return feedback.data;
    },
    enabled: !!mentorshipId,
  });
};

export const useGetMyMentorships = (isAdmin?: boolean) => {
  return useQuery({
    queryKey: ["mentorship"],
    queryFn: async () => {
      const mentorships = await fetchApi<{ mentorships: MentorshipDetails[] }>({
        url: `/v1/mentorships/my_mentorships`,
        method: "GET",
      });

      if (!mentorships.data?.mentorships)
        throw new Error("No mentorships found");

      return mentorships.data.mentorships;
    },
    enabled: !isAdmin,
  });
};

export const useFindMentorship = (mentorId: string, menteeId: string) => {
  return useQuery({
    queryKey: ["mentorship", mentorId, menteeId],
    queryFn: async () => {
      const mentorships = await fetchApi<{ mentorship: MentorshipDetails }>({
        url: `/v1/mentorships/find_mentorship`,
        method: "GET",
        params: { mentorId, menteeId },
      });

      if (!mentorships.data?.mentorship) throw new Error("No mentorship found");

      return mentorships.data.mentorship;
    },
    enabled: !!mentorId && !!menteeId,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
};

export const findMentorship = async (mentorId: string, menteeId: string) => {
  const response = await fetchApi<{ mentorship: MentorshipDetails }>({
    url: `/v1/mentorships/find_mentorship`,
    method: "GET",
    params: { mentorId, menteeId },
  });
  return response.data?.mentorship;
};

interface CalendarEvent {
  sessions: number;
  messages: number;
  calls: number;
  feedbacks: number;
  upcoming: number;
}

interface CalendarResponse {
  [date: string]: CalendarEvent;
}

export const useMenteeCalendar = (
  mentorshipId: string,
  month?: number,
  year?: number,
) => {
  return useQuery({
    queryKey: ["mentee-calendar", mentorshipId, month, year],
    queryFn: async () => {
      const { data } = await fetchApi<CalendarResponse>({
        url: `v2/mentor/mentorship/${mentorshipId}/calendar`,
        method: "GET",
        params: { month, year },
      });

      if (!data) {
        throw new Error("No data found");
      }

      return data;
    },
  });
};

export const useMentorNotificationsCount = (mentorId?: string) => {
  const { data } = useQuery({
    queryKey: ["notification-count", mentorId],
    queryFn: () =>
      fetchApi<{ count: number }>({
        url: `/v2/mentor/${mentorId}/notifications/count`,
      }),
    enabled: !!mentorId,
  });
  return data?.data?.count || 0;
};

export const useUpdateMentorshipOverride = (mentorshipId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (isOverridden: boolean) => {
      const response = await fetchApi<{
        metadata: MentorshipDetails["metadata"];
      }>({
        url: `/v1/mentorships/${mentorshipId}/override`,
        method: "PUT",
        data: { isOverridden },
      });

      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate and refetch mentorship data
      queryClient.invalidateQueries({
        queryKey: ["mentorship", mentorshipId],
      });

      toast({
        title: data.metadata?.override?.isOverridden
          ? "Override session started"
          : "Override session ended",
        description: data.metadata?.override?.isOverridden
          ? "You can now manage this mentorship's chat"
          : "Chat management has been disabled",
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Error updating override status",
        description: "Something went wrong. Please try again.",
      });
    },
  });
};

export interface SuggestedTopicsResponse {
  topics: SuggestedTopic[];
}

export interface SuggestedTopic {
  topic: string;
  context: string;
  mentee_angle: string;
  mentee_tip: string;
}

export const useGetSuggestedTopics = (mentorshipId: string) => {
  return useQuery({
    queryKey: ["suggested-topics", mentorshipId],
    queryFn: () =>
      fetchApi<SuggestedTopicsResponse>({
        url: `/v2/mentor/${mentorshipId}/topics`,
        method: "GET",
      }),
    enabled: false,
  });
};

export const useRefreshSuggestedTopics = (mentorshipId: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await fetchApi<SuggestedTopicsResponse>({
        url: `/v2/mentor/${mentorshipId}/topics`,
        method: "GET",
        params: { isForcedRefresh: true },
      });
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["suggested-topics", mentorshipId],
      });
      toast({
        title: "Suggested topics refreshed",
        description: "The suggested topics have been refreshed successfully",
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Error refreshing suggested topics",
        description: "Something went wrong. Please try again.",
      });
    },
  });
};
export const useGetConversationSummary = (mentorshipId: string) => {
  return useQuery({
    queryKey: ["conversation-summary", mentorshipId],
    queryFn: () =>
      fetchApi<{ summary: string }>({
        url: `/v1/mentorships/${mentorshipId}/conversation-summary`,
        method: "GET",
      }),
    enabled: !!mentorshipId,
  });
};

export const useGetMentorEngagementData = (
  mentorId: string,
  month?: number,
) => {
  return useQuery({
    queryKey: ["mentor-engagement-data", mentorId, month],
    queryFn: () =>
      fetchApi<{ engagementData: any }>({
        url: `/v2/mentor/${mentorId}/engagement-data`,
        method: "GET",
        params: { month },
      }),
    enabled: !!mentorId,
  });
};

export const useGetMentorRetentionData = (mentorId: string, month?: number) => {
  return useQuery({
    queryKey: ["mentor-retention-data", mentorId, month],
    queryFn: () =>
      fetchApi<{ retentionData: any }>({
        url: `/v2/mentor/${mentorId}/retention-data`,
        method: "GET",
        params: { month },
      }),
    enabled: !!mentorId,
  });
};

export const useGetMentorIncentiveData = (mentorId: string, month?: number) => {
  return useQuery({
    queryKey: ["mentor-incentive-data", mentorId, month],
    queryFn: () =>
      fetchApi<{ incentiveData: any }>({
        url: `/v2/mentor/${mentorId}/incentive-data`,
        method: "GET",
        params: { month },
      }),
    enabled: !!mentorId,
  });
};

export const useGetMentorProfileInterestData = (
  mentorId: string,
  month?: number,
  isForcedRefresh?: boolean,
) => {
  return useQuery({
    queryKey: [
      "mentor-profile-interest-data",
      mentorId,
      month,
      isForcedRefresh,
    ],
    queryFn: () =>
      fetchApi<{ profileInterestData: any }>({
        url: `/v2/mentor/${mentorId}/profile-interest-data`,
        method: "GET",
        params: { month, isForcedRefresh },
      }),
    enabled: !!mentorId,
  });
};

export const useGetMenteeNotificationStatus = (menteeId: string) => {
  return useQuery({
    queryKey: ["mentee-notification-status", menteeId],
    queryFn: () =>
      fetchApi<{ pushOptedIn: boolean }>({
        url: `/v2/mentor/onesignal/users/${menteeId}/push-opt-in`,
        method: "GET",
      }),
    enabled: !!menteeId,
  });
};
